import React, {FC, Fragment} from 'react';
import {observer} from 'mobx-react';
import {Box, Container} from '@mui/material';
import {NavLink} from 'react-router-dom';
import styled from '@emotion/styled';
import {useViewController} from 'data/services/locator';
import {Bindings} from 'data/constants/bindings';
import {SB_LINK, SYNDICATE, HIDE_SUPER_BOWL_FOOTER, LINKS} from 'data/constants';
import {SyndicatesType} from 'data/enums';
import {type IFooterController} from 'views/components/footer/footer.controller';
import {Exist} from 'views/components/exist/exist.component';
import {media} from 'assets/css/media';
import NflLogo from 'assets/img/nfl_logo.svg?react';
import GSLogo from 'assets/img/gs-logo.svg?react';
import {sponsorLink, sponsorLogo, DISABLE_SPORT_DE} from 'data/constants/header';
import {PrimaryButton, SponsorWrapper} from 'views/components/style';
import DeSponsorLogo from 'assets/img/sport_de_logo.svg?react';
import NflLogoSB from 'assets/img/logo_nfl_sb.svg?react';
import footerBg from 'assets/img/off_season_footer_bg.jpg';
import footerBgMX from 'assets/img/mx/background.jpg';


const FooterWrapper = styled(Box)`
	width: 100%;
	background: ${SYNDICATE === SyndicatesType.US ? `url("${footerBgMX}")` :  '#151515'};
	background-size: 100%;
	padding: 16px 0;

	&.off_season {
		background:url("${footerBg}") no-repeat 50% 50%;
		background-size: cover;
	}
`;

const FooterContainer = styled(Container)`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	gap: 30px;
	color: #fff;

	&.with_padding {
		padding: 50px 0 30px;

		@media (max-width: ${media.mobile}) {
			padding: 5px 0 10px;
		}
	}

	@media (max-width: ${media.mobile}) {
		gap: 15px;
	}

	.play_btn {
		padding: 12px 30px;
		min-width: unset;
		width: auto;
	}
`;
const LinksRow = styled(Box)`
	border-bottom: 1px solid #555555;
	padding-bottom: 12px;

	a {
		color: #555555;
		transition: all ease 0.4s;

		&:hover {
			color: #fff;
		}
	}
`;
const Description = styled("p")`
	text-align: center;
`;

const LogoContainer = styled(Box)`
	display: flex;
	gap: 20px;
	justify-content: center;
	align-items: center;

	span {
		font-size: 32px;
	}
`;

const SponsorIcon = styled.img`
	margin-top: 12px;
	height: 32px;
	width: auto;

	@media (min-width: ${media.tablet}) {
		height: 40px;
	}

	&.${SyndicatesType.CA}, &.${SyndicatesType.DE} {
		height: 16px;

		@media (min-width: ${media.tablet}) {
			height: 20px;
		}
	}

	&.${SyndicatesType.US} {
		height: 50px;

		@media (min-width: ${media.mobile}) {
			height: 60px;
		}
	}
`;

export const Footer: React.FC = observer(() => {
	const {i18n, isOffSeason} = useViewController<IFooterController>(Bindings.FooterController);

	return (
		<Fragment>
			<Exist when={isOffSeason && !HIDE_SUPER_BOWL_FOOTER}>
				<FooterWrapper className="off_season">
					<FooterContainer className="with_padding">
						<LogoContainer>
							<NflLogoSB />
						</LogoContainer>
						<Description>
							{i18n.t(
								"footer.description_sb",
								"Take part in the road to the Super Bowl"
							)}
						</Description>
						<PrimaryButton className="play_btn" size="large" href={SB_LINK}>
							{i18n.t("footer.button.play_now", "Play now")}
						</PrimaryButton>
					</FooterContainer>
				</FooterWrapper>
			</Exist>

			<FooterWrapper>
				<FooterContainer>
					<LogoContainer>
						<Exist when={SYNDICATE === SyndicatesType.US}>
							<SponsorWrapper link={sponsorLink}>
								<SponsorIcon className={SYNDICATE} src={sponsorLogo} />
							</SponsorWrapper>
							<span>|</span>
						</Exist>

						<Box>
							<NflLogo width="84px" height="84px" />
						</Box>
					</LogoContainer>
					<LinksRow>
						<NavLink to={LINKS.terms}>
							{i18n.t("page.help.t&c", "Terms & Conditions")}
						</NavLink>
					</LinksRow>
					<Description>
						{i18n.t(
							"footer.text",
							`© 2023 NFL Enterprises LLC. NFL and the NFL shield design are registered trademarks of the National Football League.The team names, logos and uniform designs are registered trademarks of the teams indicated. All other NFL-related trademarks are trademarks of the National Football League. NFL footage © NFL Productions LLC.`
						)}
					</Description>
					<a
						href="https://www.geniussports.com/genius-marketing-suite/genius-games/"
						rel="noreferrer"
						target="_blank">
						<GSLogo />
					</a>
				</FooterContainer>
			</FooterWrapper>

			<Exist when={SYNDICATE === SyndicatesType.DE && !DISABLE_SPORT_DE}>
				<DeFooter />
			</Exist>
		</Fragment>
	);
});

const SFooter = styled.footer`
	align-items: flex-start;
	background: #054850;
	box-sizing: border-box;
	color: #f3f3f3;
	display: flex;
	flex-flow: row;
	flex-wrap: wrap;
	font: 15px/1.6 Titillium Web;
	gap: 8px;
	min-height: 120px;
	justify-content: space-between;
	overflow: hidden;
	padding-bottom: 16px;
	padding-left: max(16px, 50vw - 1004px / 2);
	padding-right: max(16px, 50vw - 1004px / 2);
	padding-top: 16px;

	.logos {
		flex-grow: 1;
		order: 1;
	}

	&:before {
		order: 4;
		content: "";
		width: 100%;
	}

	.logos {
		flex-grow: 1;
		order: 1;

		a {
			display: inline-block;
			margin-bottom: 16px;

			&:nth-child(3) {
				padding-left: 6px;
			}
		}
	}

	nav {
		flex-grow: 1;
		order: 1;

		ul {
			flex-direction: column;
			display: flex;
			gap: 8px;
			list-style-type: none;
			margin: 0;
			min-width: 0;
			overflow-y: auto;
			padding: 0;

			li {
				display: list-item;
				text-align: -webkit-match-parent;

				a {
					color: inherit;
					display: inline-block;
					text-decoration: none;
					white-space: nowrap;
				}
			}
		}

		@media (max-width: ${media.mobile}) {
			order: 3;
			width: calc(50% - 16px);
		}
	}

	& > * {
		order: 3;
	}

	.social {
		flex: 1;
		display: flex;
		gap: 4px;
		justify-content: flex-end;

		@media (max-width: ${media.mobile}) {
			flex-grow: 0;
			order: 2;
		}
	}

	small {
		font-size: 11px;
		order: 5;
	}

	&:after {
		content: "";
		order: 2;
		width: 100%;
	}
`;

const DeFooter: FC = () => (
	<SFooter>
		<section className="logos">
			<a href="https://www.sport.de" target="_blank" rel="noreferrer noopener">
				{/*<img*/}
				{/*	src="https://s.hs-data.com/comon/prj/isdc/v2/sportde/static/gfx/sport_de_logo.svg"*/}
				{/*	width="154"*/}
				{/*	height="30"*/}
				{/*	alt="sport.de Logo"*/}
				{/*/>*/}
				<DeSponsorLogo width={154} height={30} />
			</a>
			<br />
			<a href="https://www.rtl.de" target="_blank" rel="noreferrer noopener">
				<svg
					width="108"
					height="19"
					viewBox="0 0 108 19"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M20.3013 7.69764C20.3013 8.65078 19.6045 9.11973 18.1674 9.11973H11.8793V6.2908H18.1674C19.6045 6.2908 20.3013 6.74449 20.3013 7.69764Z"
						fill="white"></path>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M0 0H33.4574V19H0V0ZM18.4734 4.36926H9.55667V14.6308H11.8793V11.0394H16.5435L20.6525 14.6308H24.0062L19.6347 10.9078C21.5664 10.6276 22.6994 9.4419 22.6994 7.66714C22.6994 5.60072 21.1888 4.36926 18.4734 4.36926Z"
						fill="white"></path>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M73.5976 0H107.055V19H73.5976V0ZM86.3477 4.36926H84.0232V14.6308H96.6863V12.6082H86.3477V4.36926Z"
						fill="white"></path>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M36.7995 0H70.2569V19H36.7995V0ZM46.4699 6.39183H52.352V14.6308H54.6746V6.39183H60.5568V4.36926H46.4699V6.39183Z"
						fill="white"></path>
				</svg>
			</a>
		</section>

		<nav>
			<ul className="flex--column">
				<li>
					<a
						href="https://www.sport.de/heute-live/"
						target="_blank"
						rel="noreferrer noopener">
						Livekalender
					</a>
				</li>
				<li>
					<a
						href="https://www.sport.de/fussball/deutschland-bundesliga/magazin/"
						target="_blank"
						rel="noreferrer noopener">
						Bundesliga
					</a>
				</li>
				<li>
					<a
						href="https://www.sport.de/wintersport/magazin/"
						target="_blank"
						rel="noreferrer noopener">
						Wintersport
					</a>
				</li>
				<li>
					<a
						href="https://www.sport.de/motorsport/formel-1/magazin/"
						target="_blank"
						rel="noreferrer noopener">
						Formel 1
					</a>
				</li>
				<li>
					<a
						href="https://www.sport.de/us-sport/magazin/"
						target="_blank"
						rel="noreferrer noopener">
						US-Sport
					</a>
				</li>
			</ul>
		</nav>

		<nav>
			<ul className="flex--column">
				<li>
					<a href="https://www.sport.de/jobs/" target="_blank" rel="noreferrer noopener">
						Jobs
					</a>
				</li>
				<li>
					<a
						href="https://www.sport.de/impressum/"
						target="_blank"
						rel="noreferrer noopener">
						Impressum
					</a>
				</li>
			</ul>
		</nav>

		<section className="social">
			<a
				className="hs-icon hs-icon-logo-facebook"
				href="https://de-de.facebook.com/sport.de"
				target="_blank"
				rel="noreferrer noopener">
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M11.5105 17.9H8.21047C7.80047 17.9 7.46047 17.56 7.46047 17.15V12.65H5.48047C5.07047 12.65 4.73047 12.31 4.73047 11.9V9.04001C4.73047 8.63001 5.07047 8.29001 5.48047 8.29001H7.43047L7.46047 6.32001V5.85001C7.44047 4.86001 7.82047 3.92001 8.51047 3.21001C9.20047 2.50001 10.1305 2.11001 11.1205 2.10001H14.5105C14.9205 2.10001 15.2605 2.44001 15.2605 2.85001V5.71001C15.2605 6.12001 14.9205 6.46001 14.5105 6.46001H12.2505V8.30001H14.5105C14.7305 8.30001 14.9405 8.40001 15.0805 8.56001C15.2205 8.72001 15.2905 8.95001 15.2505 9.16001L14.8105 12.02C14.7505 12.39 14.4405 12.66 14.0705 12.66H12.2605V17.16C12.2605 17.57 11.9205 17.91 11.5105 17.91V17.9ZM8.96047 16.4H10.7605V11.9C10.7605 11.49 11.1005 11.15 11.5105 11.15H13.4305L13.6405 9.79001H11.5105C11.1005 9.79001 10.7605 9.45001 10.7605 9.04001V6.44001C10.7605 5.62001 11.4305 4.95001 12.2505 4.95001H13.7605V3.59001H11.1605C10.5505 3.59001 9.99047 3.83001 9.58047 4.25001C9.17047 4.67001 8.94047 5.23001 8.95047 5.82001V6.31001L8.92047 9.04001C8.92047 9.45001 8.58047 9.78001 8.17047 9.78001H6.23047V11.14H8.21047C8.62047 11.14 8.96047 11.48 8.96047 11.89V16.39V16.4Z"
						fill="#f3f3f3"></path>
				</svg>
			</a>
			<a
				className="hs-icon hs-icon-logo-twitter"
				href="https://twitter.com/Sportde?lang=de"
				target="_blank"
				rel="noreferrer noopener">
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M12.3742 4.71537C10.9539 4.71537 9.80253 5.86675 9.80253 7.28705C9.80253 7.46086 9.81961 7.62984 9.85203 7.79266C9.9042 8.05491 9.82736 8.32627 9.64545 8.52225C9.46353 8.71825 9.19862 8.815 8.9332 8.78241C7.093 8.55666 5.4191 7.7963 4.07118 6.66283C4.09744 7.16443 4.27132 7.69726 4.64869 8.25545L5.21434 9.09216L4.28546 9.48858L4.10623 9.56508C4.23945 9.71741 4.39962 9.87391 4.57844 10.0277C4.78134 10.2021 4.97742 10.3471 5.12308 10.4484C5.19543 10.4987 5.25413 10.5375 5.29346 10.5629C5.31309 10.5756 5.32782 10.5848 5.33689 10.5905L5.34616 10.5962L5.34685 10.5967L6.63743 11.3792L5.28701 12.0544C5.22015 12.0878 5.15342 12.1177 5.08697 12.1442C5.35807 12.3726 5.68801 12.5829 6.03903 12.7488L7.19722 13.2963L6.22725 14.1332C5.69778 14.59 5.16518 14.9305 4.47492 15.1105C5.49771 15.6562 6.66966 15.9663 7.91654 15.9663C11.9208 15.9663 15.1459 12.7708 15.1459 8.85466V8.42158L15.5002 8.17267C16.0534 7.78422 16.4444 7.2589 16.7244 6.65836H15.4502H14.924L14.6979 6.18328C14.2838 5.31361 13.398 4.71537 12.3742 4.71537ZM2.97438 10.7984C2.81729 10.9783 2.74281 11.2202 2.77697 11.4627C2.87098 12.1302 3.27071 12.7052 3.7124 13.144C3.83235 13.2632 3.96063 13.3776 4.09533 13.4867C4.07475 13.4924 4.05401 13.4979 4.03307 13.5032C3.63194 13.6036 3.09142 13.6245 2.18999 13.5362C1.84148 13.502 1.50882 13.6894 1.35737 14.0052C1.20593 14.3208 1.26803 14.6976 1.5128 14.948C3.13114 16.6037 5.40381 17.633 7.91654 17.633C12.677 17.633 16.5819 13.9366 16.8027 9.27166C17.8771 8.36908 18.415 7.14727 18.7052 6.03552C18.7705 5.78576 18.7162 5.51989 18.5584 5.31561C18.4006 5.11132 18.157 4.9917 17.8989 4.9917H15.9376C15.1838 3.82377 13.8701 3.04871 12.3742 3.04871C10.15 3.04871 8.32596 4.76196 8.14978 6.94076C6.5923 6.53713 5.22578 5.65631 4.21589 4.46173C4.03169 4.24384 3.74882 4.13533 3.46615 4.17414C3.18348 4.21295 2.94032 4.39367 2.82166 4.65315C2.31776 5.75497 2.22065 7.02586 2.78467 8.31705L2.58992 8.40016C2.20425 8.56475 2.00097 8.99033 2.11528 9.39375C2.26933 9.9375 2.61811 10.4176 2.97438 10.7984Z"
						fill="#f3f3f3"></path>
				</svg>
			</a>
			<a
				className="hs-icon hs-icon-logo-instagram"
				href="https://instagram.com/sport.de/"
				target="_blank"
				rel="noreferrer noopener">
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M13.9499 2.13H6.03988C3.87988 2.13 2.12988 3.89 2.12988 6.04V13.95C2.12988 16.11 3.88988 17.86 6.03988 17.86H13.9499C16.1099 17.86 17.8599 16.1 17.8599 13.95V6.04C17.8599 3.88 16.0999 2.13 13.9499 2.13ZM16.3599 13.95C16.3599 15.28 15.2799 16.36 13.9499 16.36H6.03988C4.70988 16.36 3.62988 15.28 3.62988 13.95V6.04C3.62988 4.71 4.70988 3.63 6.03988 3.63H13.9499C15.2799 3.63 16.3599 4.71 16.3599 6.04V13.95ZM9.99984 6.07999C7.83984 6.07999 6.08984 7.83999 6.08984 9.98999C6.08984 12.14 7.84984 13.9 9.99984 13.9C12.1498 13.9 13.9098 12.14 13.9098 9.98999C13.9098 7.83999 12.1498 6.07999 9.99984 6.07999ZM9.99984 12.41C8.66984 12.41 7.58984 11.33 7.58984 9.99999C7.58984 8.66999 8.66984 7.58999 9.99984 7.58999C11.3298 7.58999 12.4098 8.66999 12.4098 9.99999C12.4098 11.33 11.3298 12.41 9.99984 12.41ZM15.1396 5.64999C15.1396 6.08629 14.7859 6.43999 14.3496 6.43999C13.9133 6.43999 13.5596 6.08629 13.5596 5.64999C13.5596 5.21368 13.9133 4.85999 14.3496 4.85999C14.7859 4.85999 15.1396 5.21368 15.1396 5.64999Z"
						fill="#f3f3f3"></path>
				</svg>
			</a>
		</section>

		<small>
			<a href="https://www.heimspiel.de/" target="_blank" rel="noreferrer noopener">
				© 2023 Heimspiel Medien GmbH &amp; Co. KG
			</a>
			/
			<a
				href="https://www.mediengruppe-rtl.de/unternehmen/firmen-und-sender/rtl-interactive/"
				target="_blank"
				rel="noreferrer noopener">
				RTL Interactive GmbH
			</a>
		</small>
		<small>
			<a
				className="hs-grid--item hs-grid--item-4 hs-grid--item-xs-12 hs-copyright"
				href="https://www.ad-alliance.de/cms/portfolio/digital/portfolio.html#/digital/portfolio/sportde/profil/"
				target="_blank"
				rel="noreferrer noopener">
				Werben auf sport.de
			</a>
		</small>
	</SFooter>
);
